"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import FilterToggle from "@components/molecules/FilterToggle";
import CustomCardAndChatBotV2 from "@components/organisms/CustomCardAndChatBot/CustomCardAndChatBotV2";
import styles from "./PriceByVariantSection.module.scss";
import {
  extractFields,
  filterConfigs,
  getFilteredVariants,
} from "../VariantWisePriceSection/VariantWisePriceHelper";
import { PageType } from "@constants/pageTypes";
import { useRouter, useSearchParams, usePathname } from "next/navigation";

interface PriceByVariantSectionClientWrapperV2Props {
  variantsDetails: any;
  carName: string;
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
}

export default function PriceByVariantSectionClientWrapperV2({
  variantsDetails,
  carName,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance,
}: PriceByVariantSectionClientWrapperV2Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [filterDetails, setFilterDetails] = useState<{ [key: string]: any }>(
    {}
  );

  const [isInitialized, setIsInitialized] = useState(false);
  const filteredVariantsDetails = variantsDetails || [];

  const fields = useMemo(() => {
    return extractFields(filteredVariantsDetails || []);
  }, [filteredVariantsDetails]);

  const filterConfigOptions = useMemo(() => {
    return filterConfigs(fields);
  }, [fields]);

  const variants = useMemo(() => {
    return getFilteredVariants(filteredVariantsDetails || [], filterDetails);
  }, [filteredVariantsDetails, filterDetails]);

  const updateUrlWithFilters = useCallback(
    (newFilterDetails: { [key: string]: any }) => {
      const params = new URLSearchParams(searchParams?.toString());

      params.delete("maxPrice");
      params.delete("minPrice");

      const isClearing = Object.keys(newFilterDetails).length === 0;

      if (
        !isClearing &&
        newFilterDetails.priceRange &&
        newFilterDetails.priceRange[0]
      ) {
        const [min, max] = newFilterDetails.priceRange[0].split("-");
        if (min && min !== "0") params.set("minPrice", min);
        if (max) params.set("maxPrice", max);
      }

      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      router.replace(newUrl || "", { scroll: false });
    },
    [searchParams, router, pathname]
  );

  useEffect(() => {
    if (isInitialized || !fields.maxPrice || fields.maxPrice.length === 0)
      return;

    const urlMaxPrice = searchParams?.get("maxPrice");
    const urlMinPrice = searchParams?.get("minPrice");

    if (!urlMaxPrice && !urlMinPrice) {
      setIsInitialized(true);
      return;
    }

    const initialFilters: { [key: string]: any } = {};

    if (urlMaxPrice || urlMinPrice) {
      const min = urlMinPrice ? Number(urlMinPrice) : 0;
      const max = urlMaxPrice ? Number(urlMaxPrice) : fields.maxPrice[0] || 0;
      initialFilters.priceRange = [`${min}-${max}`];
    }

    if (Object.keys(initialFilters).length > 0) {
      setFilterDetails(initialFilters);
    }

    setIsInitialized(true);
  }, [fields.maxPrice, isInitialized, searchParams]);

  return (
    <>
      <div className={styles.filterToggleWrapper}>
        <FilterToggle
          filterConfigs={filterConfigOptions}
          initialSelectedFilters={filterDetails}
          onApply={(key, selectedValues) => {
            const newFilterDetails = {
              ...filterDetails,
              [key]: selectedValues,
            };
            setFilterDetails(newFilterDetails);

            if (key === "priceRange") {
              updateUrlWithFilters(newFilterDetails);
            }
          }}
          onClear={() => {
            setFilterDetails({});
            updateUrlWithFilters({});
            setIsInitialized(true);
          }}
          pageType={pageType}
          brandName={brandName}
          modelName={modelName}
          carAvailability={carAvailability}
        />
      </div>
      <div className={styles.cardWrapper}>
        <CustomCardAndChatBotV2
          carName={carName}
          variants={variants}
          isLoggedIn={isLoggedIn}
          city={city}
          brandName={brandName}
          modelName={modelName}
          isSellable={isSellable}
          pageType={pageType}
          carAvailability={carAvailability}
          savingBalance={savingBalance}
        />
      </div>
    </>
  );
}
